import React, { useEffect, useState, useRef } from "react";
import { auth, db, provider, storage } from "../firebase";
import {
  signInWithPopup,
  onAuthStateChanged,
  signOut,
} from "firebase/auth";
import {
  collection,
  addDoc,
  query,
  orderBy,
  onSnapshot,
  serverTimestamp,
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { ArrowRightOnRectangleIcon, PaperClipIcon, PaperAirplaneIcon } from "@heroicons/react/24/outline";
import { motion, AnimatePresence } from "framer-motion";

export default function ChatWidget() {
  const [user, setUser] = useState(null);
  const [message, setMessage] = useState("");
  const [messages, setMessages] = useState([]);
  const [open, setOpen] = useState(false);
  const [file, setFile] = useState(null);
  const chatEndRef = useRef(null);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => setUser(u));
    return () => unsub();
  }, []);

  useEffect(() => {
    if (user) {
      const q = query(collection(db, "messages"), orderBy("timestamp"));
      const unsub = onSnapshot(q, (snapshot) => {
        const data = snapshot.docs
          .map((doc) => ({ id: doc.id, ...doc.data() }))
          .filter(
            (msg) =>
              msg.senderEmail === user.email ||
              msg.receiverEmail === user.email
          );
        setMessages(data);
      });
      return () => unsub();
    }
  }, [user]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const handleSend = async () => {
    if (!message.trim() && !file) return;

    let fileUrl = "";
    let fileType = "";

    try {
      if (file) {
        const safeName = file.name.replace(/\s+/g, "_");
        const storageRef = ref(storage, `chatUploads/${Date.now()}_${safeName}`);
        await uploadBytes(storageRef, file);
        fileUrl = await getDownloadURL(storageRef);
        fileType = file.type.startsWith("image/") ? "image" : file.type.startsWith("video/") ? "video" : "file";
      }

      await addDoc(collection(db, "messages"), {
        text: message,
        senderName: user.displayName,
        senderEmail: user.email,
        timestamp: serverTimestamp(),
        fileUrl,
        fileType,
      });

      setMessage("");
      setFile(null);
    } catch (error) {
      console.error(error);
      alert("Transmission failed.");
    }
  };

  return (
    <>
      {/* Floating Button */}
      <motion.button
        whileHover={{ scale: 1.1, rotate: 5 }}
        whileTap={{ scale: 0.9 }}
        onClick={() => setOpen(!open)}
        className="fixed bottom-8 right-8 bg-blue-600 text-white p-5 rounded-[2rem] shadow-[0_20px_40px_rgba(37,99,235,0.3)] z-50 border border-white/20 backdrop-blur-xl"
      >
        <div className="w-8 h-8 flex items-center justify-center text-2xl">
          {open ? "✕" : "💬"}
        </div>
      </motion.button>

      {/* Chat Popup */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 100, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 100, scale: 0.9 }}
            className="fixed bottom-32 right-8 w-[400px] h-[650px] bg-[#020617]/95 backdrop-blur-3xl rounded-[3.5rem] shadow-[0_50px_100px_rgba(0,0,0,0.5)] flex flex-col overflow-hidden z-50 border border-white/10"
          >
            {/* Header */}
            <div className="bg-[#020617] text-white px-8 py-10 flex justify-between items-center relative overflow-hidden border-b border-white/5">
              <div className="absolute inset-0 bg-mesh opacity-20 pointer-events-none" />
              <div className="relative z-10">
                <p className="text-xl font-extrabold tracking-tight uppercase leading-none">LAB <span className="text-blue-500">DEVS</span></p>
                <div className="flex items-center gap-2 mt-2">
                  <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse shadow-[0_0_10px_rgba(34,197,94,0.5)]" />
                  <p className="text-[9px] font-bold text-slate-500 uppercase tracking-widest">Support Online</p>
                </div>
              </div>
              {user && (
                <button
                  onClick={() => signOut(auth)}
                  className="relative z-10 text-[9px] font-bold uppercase tracking-widest bg-white/5 hover:bg-white/10 px-4 py-2 rounded-lg border border-white/10 transition-all"
                >
                  Sign Out
                </button>
              )}
            </div>

            {/* Messages */}
            <div className="flex-1 px-6 py-8 overflow-y-auto space-y-6 bg-[#020617]/50 custom-scrollbar">
              {user ? (
                <>
                  {messages.map((msg, idx) => (
                    <motion.div
                      initial={{ opacity: 0, x: msg.senderEmail === user.email ? 20 : -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      key={idx}
                      className={`flex ${msg.senderEmail === user.email ? "justify-end" : "justify-start"}`}
                    >
                      <div
                        className={`p-6 rounded-[2.5rem] max-w-[85%] shadow-2xl ${msg.senderEmail === user.email
                            ? "bg-blue-600 text-white rounded-tr-none"
                            : "bg-white/5 text-white border border-white/10 rounded-tl-none"
                          }`}
                      >
                        {msg.text && <p className="text-sm font-medium leading-relaxed">{msg.text}</p>}
                        {msg.fileUrl && msg.fileType === "image" && (
                          <img src={msg.fileUrl} alt="upload" className="w-full mt-4 rounded-2xl border border-white/10" />
                        )}
                        {msg.fileUrl && msg.fileType === "video" && (
                          <video controls className="w-full mt-4 rounded-2xl">
                            <source src={msg.fileUrl} />
                          </video>
                        )}
                        {msg.fileUrl && msg.fileType === "file" && (
                          <a href={msg.fileUrl} target="_blank" rel="noreferrer" className="text-blue-400 underline mt-4 block text-[10px] font-bold uppercase tracking-widest">
                            📄 View Attachment
                          </a>
                        )}
                        <p className={`text-[8px] font-black uppercase tracking-widest mt-2 ${msg.senderEmail === user.email ? "text-blue-100/60" : "text-slate-500"}`}>
                          {msg.timestamp?.toDate().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </p>
                      </div>
                    </motion.div>
                  ))}
                  <div ref={chatEndRef} />
                </>
              ) : (
                <div className="h-full flex flex-col items-center justify-center text-center p-8">
                  <div className="w-20 h-20 bg-white/5 rounded-3xl flex items-center justify-center text-4xl mb-6 shadow-2xl border border-white/10">🔒</div>
                  <h3 className="text-xl font-extrabold text-white uppercase tracking-tight mb-4">Secure Sign-In</h3>
                  <p className="text-xs text-slate-500 font-normal mb-8">Please sign in to start a secure conversation with our support team.</p>
                </div>
              )}
            </div>

            {/* File Preview */}
            <AnimatePresence>
              {file && (
                <motion.div
                  initial={{ height: 0 }}
                  animate={{ height: "auto" }}
                  exit={{ height: 0 }}
                  className="px-6 py-4 border-t border-white/5 bg-[#020617]"
                >
                  <div className="flex items-center justify-between p-4 bg-white/5 rounded-2xl border border-white/5">
                    <div className="flex items-center gap-4 overflow-hidden">
                      {file.type.startsWith("image/") ? (
                        <img src={URL.createObjectURL(file)} alt="preview" className="w-12 h-12 rounded-xl object-cover border border-white/10" />
                      ) : (
                        <div className="w-12 h-12 bg-white/5 rounded-xl flex items-center justify-center border border-white/10 text-xl">📄</div>
                      )}
                      <div className="overflow-hidden">
                        <p className="text-[10px] font-black text-white truncate uppercase tracking-widest">{file.name}</p>
                        <p className="text-[8px] font-black text-slate-500 uppercase tracking-widest">Ready for upload</p>
                      </div>
                    </div>
                    <button onClick={() => setFile(null)} className="text-slate-500 hover:text-red-500 transition-colors">✕</button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Input Area */}
            <div className="p-6 bg-[#020617] border-t border-white/5">
              {user ? (
                <div className="flex items-center gap-4 bg-white/5 p-2 rounded-[2.5rem] border border-white/5">
                  <div className="relative">
                    <input
                      id="fileUpload"
                      type="file"
                      onChange={(e) => setFile(e.target.files[0])}
                      className="hidden"
                      accept="image/*,video/*,.pdf,.doc,.docx"
                    />
                    <label
                      htmlFor="fileUpload"
                      className="cursor-pointer flex items-center justify-center bg-white/5 text-white rounded-full w-12 h-12 border border-white/5 hover:bg-white/10 transition shadow-2xl"
                    >
                      <PaperClipIcon className="w-5 h-5" />
                    </label>
                  </div>

                    <input
                      type="text"
                      placeholder="Type your message..."
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && handleSend()}
                      className="flex-1 bg-transparent px-2 py-3 text-sm font-normal focus:outline-none text-white placeholder:text-slate-500"
                    />

                  <button
                    onClick={handleSend}
                    className="bg-blue-600 text-white rounded-full w-12 h-12 flex items-center justify-center hover:bg-blue-700 transition shadow-[0_0_15px_rgba(37,99,235,0.3)] shrink-0"
                  >
                    <PaperAirplaneIcon className="w-5 h-5 -rotate-45" />
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => signInWithPopup(auth, provider)}
                  className="w-full py-5 bg-blue-600 text-white font-bold rounded-2xl hover:bg-blue-700 transition-all uppercase tracking-widest text-[10px] shadow-[0_20px_40px_rgba(37,99,235,0.3)]"
                >
                  Sign In with Google
                </button>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
